import { FlexTypography, Typography } from "@lyfegen-platform/react-common";
import { Box, Checkbox, IconButton, TypographyProps, useTheme } from "@material-ui/core";
import { ArrowDownward, ArrowUpward } from "@material-ui/icons";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import useResizeObserver from "../../hooks/use-resize-observer";
import { HeaderActionOptions } from "../../models/common/data";
import { Column } from "../../types/tableTypes";
import MappedComponent from "../mapped-components/MappedComponent";
import { rowAddonWidthPx, TableTypes } from "./LyfeDataTable";
import useStyles from "./use-styles";
import getTableState from "./utils/getTableState";

export interface ActionOptionsProps {
	label: HeaderActionOptions;
	action?: (e: React.MouseEvent<HTMLElement>, props: any) => void;
}
interface ITableHeader {
	hasCollapsibleRows?: boolean;
	hasRowAddon?: boolean;
	headerActionsOptions: Array<ActionOptionsProps>;
	columns: Array<Column<object>>;
	title: TableTypes;
	defaultSort?: {
		column: string;
		action: HeaderActionOptions;
	};
	handleHeaderCheckBoxChange?: (checked: boolean) => void;
	checkBox?: boolean;
	isChecked?: boolean;
	showActionColumn?: boolean;
	actionButtonHeader?: any;
}

export const CollapsibleTableHeader = () => <th style={{ width: "30px" }} className={useStyles().rowHeader}></th>;
export const RowAddonTableHeader = () => <th style={{ width: `${rowAddonWidthPx}px` }} className={useStyles().rowHeader}></th>;

function LyfeTableHeader(props: ITableHeader) {
	const { rowHeaderFont, rowHeader, thText } = useStyles({});
	const { spacing } = useTheme();
	const [activeAction, setActiveAction] = useRecoilState(getTableState(props.title));
	const { ref, width = 1 } = useResizeObserver<HTMLDivElement>();
	const [sortDirAsc, setSortDirAsc] = useState(false);
	const [column, setColumn] = useState("");
	const columnRef = useRef("");
	function renderActiveActionIcon(action: ActionOptionsProps["label"] | undefined) {
		switch (action) {
			case "sortAsc":
				return <ArrowUpward fontSize="small" />;
			case "sortDesc":
				return <ArrowDownward fontSize="small" />;
			default:
				return null;
		}
	}

	useEffect(() => {
		if (column) {
			const action = sortDirAsc ? "sortAsc" : "sortDesc";
			setActiveAction({
				...activeAction,
				column,
				action,
			});
			columnRef.current = column;
		}
	}, [column, sortDirAsc]);

	function handleClickActionItem(field: string) {
		if (columnRef.current !== field) {
			setSortDirAsc(true);
		}
		if (columnRef.current === field) {
			setSortDirAsc(!sortDirAsc);
		}
		setColumn(field);
	}

	function handleCheckBoxChange(event: React.ChangeEvent<HTMLInputElement>, check: boolean) {
		props.handleHeaderCheckBoxChange && props.handleHeaderCheckBoxChange(check);
	}

	useEffect(() => {
		if (!activeAction.action) {
			setActiveAction({
				...activeAction,
				column: props.defaultSort?.column,
				action: props?.defaultSort?.action,
			});
		}
	}, []);

	const typographyProps = (c: Column<object>) =>
		({
			align: c.align || "left",
			style: { fontWeight: 600 },
			variant: "body1",
			type: "semi-bold",
			className: rowHeaderFont,
		} as TypographyProps);

	const memCols = useMemo(() => props.columns, props.columns);

	return (
		<thead>
			<tr>
				{props.checkBox && (
					<td style={{ width: spacing(5) }} className={rowHeader}>
						<Checkbox
							id="header-checkbox"
							style={{ marginLeft: "11px" }}
							checked={props.isChecked}
							onChange={handleCheckBoxChange}
						/>
					</td>
				)}
				{props.hasCollapsibleRows && <CollapsibleTableHeader />}
				{props.hasRowAddon && <RowAddonTableHeader />}
				{memCols.map((c: Column<object>, i: number) => (
					<th
						key={c.field}
						className={rowHeader}
						ref={ref}
						style={{ paddingRight: i === memCols.length - 1 ? "32px" : "undefined" }}
					>
						<Box
							justifyContent={c.align ? c.align : "left"}
							flexDirection={c.align && c.align === "right" ? "row-reverse" : "row"}
							display="flex"
							width="100%"
							alignItems="center"
							pl={i === 0 ? "20px" : 1}
							marginLeft={props.checkBox && i === 0 ? "-15px" : undefined}
						>
							{c.tooltip && (
								<MappedComponent
									component="Tooltip"
									props={{
										title: c.tooltip,
										placement: "top",
									}}
								>
									<span style={{ cursor: "pointer" }} onClick={() => handleClickActionItem(c.field!)}>
										<Typography noWrap {...typographyProps(c)}>
											{c.title}
										</Typography>
									</span>
								</MappedComponent>
							)}

							{!c.tooltip && typeof c.title === "string" && (
								<div className={thText} onClick={() => handleClickActionItem(c.field!)}>
									<FlexTypography
										tooltipPlacement="top-start"
										typographyProps={{
											variant: "body1",
											style: { fontWeight: 600 },
										}}
										text={c.title}
										width={width - 100}
									/>
								</div>
							)}
							<Box display="flex" alignItems="center">
								{activeAction.column === c.field && (
									<IconButton size="small" disabled>
										{renderActiveActionIcon(activeAction.action)}
									</IconButton>
								)}
							</Box>
						</Box>
					</th>
				))}
				{props.showActionColumn && (
					<td style={{ width: spacing(5) }} className={rowHeader}>
						{props.actionButtonHeader || ""}
					</td>
				)}
			</tr>
		</thead>
	);
}

export default memo(LyfeTableHeader);
